/**
 * TOTP (Time-based One-Time Password) generation
 * Used for 2FA codes stored in Vaultwarden
 */

import * as OTPAuth from 'otpauth';

export interface TOTPResult {
  code: string;
  remaining: number; // seconds until next code
  period: number; // total period in seconds
}

/**
 * Parse TOTP secret from various formats and generate current code
 *
 * Supports:
 * - otpauth:// URI format
 * - Raw base32 secret
 *
 * @param secret - TOTP secret or otpauth:// URI
 * @returns Current TOTP code and time remaining
 */
export function generateTOTP(secret: string): TOTPResult {
  let totp: OTPAuth.TOTP;

  if (secret.startsWith('otpauth://')) {
    // Parse full URI: otpauth://totp/Label?secret=XXX&issuer=YYY&period=30
    totp = OTPAuth.URI.parse(secret) as OTPAuth.TOTP;
  } else {
    // Raw secret - create TOTP with defaults
    totp = new OTPAuth.TOTP({
      secret: OTPAuth.Secret.fromBase32(secret.replace(/\s/g, '').toUpperCase()),
      algorithm: 'SHA1',
      digits: 6,
      period: 30,
    });
  }

  const code = totp.generate();
  const period = totp.period;
  const remaining = period - (Math.floor(Date.now() / 1000) % period);

  return { code, remaining, period };
}

/**
 * Format TOTP code with space in the middle (e.g., "123 456")
 */
export function formatTOTPCode(code: string): string {
  if (code.length === 6) {
    return `${code.slice(0, 3)} ${code.slice(3)}`;
  }
  return code;
}
